// covered-by: frontends/carrie/src/services/__tests__/askPunttOrgFilters.test.ts
import { type AskPunttLauncherSurface } from "@mg/schemas/src/christo/askPunttLauncherView";

import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

/**
 * Ask Puntt Org Filters service — API client for org labels and saved
 * launcher views (Projects / Chats).
 */

const VIEWS_BASE = `${API_URL}/private/catalyst/ask-puntt/launcher-views`;
const LABELS_BASE = `${API_URL}/private/catalyst/ask-puntt/org-labels`;
const PROJECTS_BASE = `${API_URL}/private/catalyst/ask-puntt/projects`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OrgLabel = {
  _id: string;
  slug: string;
  name: string;
};

export type OrgLabelsResponse = {
  regions: OrgLabel[];
  tags: OrgLabel[];
};

export type LauncherSurface = AskPunttLauncherSurface;

export type LauncherSavedView = {
  _id: string;
  name: string;
  surface?: LauncherSurface;
  filters: {
    mine?: boolean;
    people?: string[];
    regions?: string[];
    tags?: string[];
    sort?: string;
  };
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type ListLauncherViewsResponse = {
  views: LauncherSavedView[];
};

function toOrgLabels(values: string[] | undefined): OrgLabel[] {
  return (values ?? [])
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .map((v) => ({ _id: v, slug: v, name: v }));
}

// ---------------------------------------------------------------------------
// Launcher Views
// ---------------------------------------------------------------------------

export async function listLauncherViews(
  surface?: LauncherSurface,
): Promise<ListLauncherViewsResponse> {
  const qs = surface ? `?surface=${encodeURIComponent(surface)}` : "";
  const json = await smartFetch(`${VIEWS_BASE}${qs}`, { method: "GET" });
  return json as ListLauncherViewsResponse;
}

export async function createLauncherView(payload: {
  name: string;
  surface: LauncherSurface;
  filters: LauncherSavedView["filters"];
  isDefault?: boolean;
}): Promise<LauncherSavedView> {
  const json = (await smartFetch(VIEWS_BASE, {
    method: "POST",
    body: JSON.stringify(payload),
  })) as { view?: LauncherSavedView } | LauncherSavedView;
  if (json && typeof json === "object" && "view" in json && json.view) {
    return json.view;
  }
  return json as LauncherSavedView;
}

export async function updateLauncherView(
  viewId: string,
  payload: Partial<{
    name: string;
    filters: LauncherSavedView["filters"];
    isDefault: boolean;
  }>,
): Promise<LauncherSavedView> {
  const json = (await smartFetch(`${VIEWS_BASE}/${viewId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  })) as { view?: LauncherSavedView } | LauncherSavedView;
  if (json && typeof json === "object" && "view" in json && json.view) {
    return json.view;
  }
  return json as LauncherSavedView;
}

export async function deleteLauncherView(viewId: string): Promise<void> {
  await smartFetch(`${VIEWS_BASE}/${viewId}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Org Labels
// ---------------------------------------------------------------------------

export async function listOrgLabels(): Promise<OrgLabelsResponse> {
  const json = (await smartFetch(LABELS_BASE, { method: "GET" })) as {
    regions?: string[];
    tags?: string[];
  };
  return {
    regions: toOrgLabels(json.regions),
    tags: toOrgLabels(json.tags),
  };
}

// ---------------------------------------------------------------------------
// Update project/chat labels
// ---------------------------------------------------------------------------

export async function updateProjectLabels(
  projectId: string,
  payload: { tags?: string[] },
): Promise<void> {
  await smartFetch(`${PROJECTS_BASE}/${projectId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function updateChatLabels(
  chatId: string,
  payload: { tags?: string[] },
): Promise<void> {
  await smartFetch(`${API_URL}/private/catalyst/ask-puntt/chats/${chatId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function updateFileLabels(
  projectId: string,
  fileId: string,
  payload: { tags?: string[] },
): Promise<void> {
  await smartFetch(`${PROJECTS_BASE}/${projectId}/files/${fileId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}
